import apiClient from './api';
import {
  RoleDefinition,
  TabPermissions,
  GroupMapping,
  EntraIdConfig,
  RetentionSettings,
  RetrySettings,
  LocalUser,
} from '../types';

/**
 * System API Service Module
 *
 * Handles all HTTP communication for the System Configuration page.
 * Uses the shared apiClient (auth interceptor, base URL, error handling).
 *
 * Requirements: 2.1, 3.1, 4.1, 5.1, 6.1, 7.1
 */

/**
 * Custom error for 409 Conflict (optimistic concurrency failure or duplicate name).
 */
export class ConflictError extends Error {
  constructor(message: string = 'A conflict occurred. Please reload and try again.') {
    super(message);
    this.name = 'ConflictError';
  }
}

// ── Role Management ──────────────────────────────────────────────────

/**
 * Get all role definitions.
 */
export async function getRoles(): Promise<Record<string, RoleDefinition>> {
  const response = await apiClient.get<{ roles: Record<string, RoleDefinition> }>('/system/roles');
  return response.data.roles ?? {};
}

/**
 * Create a new role.
 *
 * @throws ConflictError if a role with the same name already exists (409)
 */
export async function createRole(name: string, permissions: TabPermissions): Promise<void> {
  try {
    await apiClient.post('/system/roles', { name, permissions });
  } catch (error: any) {
    if (error?.response?.status === 409) {
      throw new ConflictError(
        error.response.data?.message || 'A role with this name already exists.'
      );
    }
    throw error;
  }
}

/**
 * Update an existing role's permissions.
 *
 * @throws ConflictError on optimistic concurrency failure (409)
 */
export async function updateRole(name: string, permissions: TabPermissions): Promise<void> {
  try {
    await apiClient.put(`/system/roles/${encodeURIComponent(name)}`, { permissions });
  } catch (error: any) {
    if (error?.response?.status === 409) {
      throw new ConflictError(
        error.response.data?.message ||
          'Role was modified by another user. Please reload and try again.'
      );
    }
    throw error;
  }
}

/**
 * Delete a role by name.
 */
export async function deleteRole(name: string): Promise<void> {
  await apiClient.delete(`/system/roles/${encodeURIComponent(name)}`);
}

// ── Authentication Settings (Entra ID) ──────────────────────────────

/**
 * Get Entra ID authentication settings.
 * Note: clientSecret is never returned by the API.
 */
export async function getAuthSettings(): Promise<EntraIdConfig> {
  const response = await apiClient.get<EntraIdConfig>('/system/auth-settings');
  return response.data;
}

/**
 * Save Entra ID authentication settings.
 */
export async function saveAuthSettings(config: EntraIdConfig): Promise<void> {
  await apiClient.put('/system/auth-settings', config);
}

/**
 * Fetch app registration and group display names from Microsoft Graph API.
 * Returns null values for any names that could not be resolved.
 */
export async function getEntraDisplayNames(groupIds?: string[]): Promise<{
  appDisplayName: string | null;
  groupDisplayNames: Record<string, string | null>;
}> {
  const params = groupIds?.length ? `?groupIds=${groupIds.map(encodeURIComponent).join(',')}` : '';
  const response = await apiClient.get(`/system/entra-display-names${params}`);
  return response.data;
}

// ── Group Mappings ──────────────────────────────────────────────────

/**
 * Get all Entra ID group-to-role mappings.
 */
export async function getGroupMappings(): Promise<GroupMapping[]> {
  const response = await apiClient.get<{ groupMappings: GroupMapping[] }>('/system/group-mappings');
  return response.data.groupMappings ?? [];
}

/**
 * Save Entra ID group-to-role mappings.
 */
export async function saveGroupMappings(mappings: GroupMapping[]): Promise<void> {
  await apiClient.put('/system/group-mappings', { groupMappings: mappings });
}

// ── Local User Management ───────────────────────────────────────────

/**
 * Get all local Cognito users.
 */
export async function getUsers(): Promise<LocalUser[]> {
  const response = await apiClient.get<{ users: LocalUser[] }>('/system/users');
  return response.data.users ?? [];
}

/**
 * Create a new local Cognito user.
 */
export async function createUser(
  email: string,
  temporaryPassword: string,
  roleName: string
): Promise<void> {
  await apiClient.post('/system/users', { email, temporaryPassword, roleName });
}

/**
 * Update a user's role assignment.
 */
export async function updateUser(email: string, roleName: string): Promise<void> {
  await apiClient.put(`/system/users/${encodeURIComponent(email)}`, { roleName });
}

/**
 * Delete a local Cognito user.
 */
export async function deleteUser(email: string): Promise<void> {
  await apiClient.delete(`/system/users/${encodeURIComponent(email)}`);
}

/**
 * Reset a user's password with a new temporary password.
 */
export async function resetPassword(email: string, temporaryPassword: string): Promise<void> {
  await apiClient.post(`/system/users/${encodeURIComponent(email)}/reset-password`, {
    temporaryPassword,
  });
}

// ── Retention Settings ──────────────────────────────────────────────

/**
 * Get current retention settings.
 */
export async function getRetentionSettings(): Promise<RetentionSettings> {
  const response = await apiClient.get<{ retentionSettings: RetentionSettings }>('/system/retention');
  return response.data.retentionSettings ?? { fileExpirationDays: 30, dynamoDbTtlDays: 90, s3ArchiveRetentionDays: 365 };
}

/**
 * Save retention settings.
 *
 * @throws ConflictError on optimistic concurrency failure (409)
 */
export async function saveRetentionSettings(settings: RetentionSettings): Promise<void> {
  try {
    await apiClient.put('/system/retention', { retentionSettings: settings });
  } catch (error: any) {
    if (error?.response?.status === 409) {
      throw new ConflictError(
        error.response.data?.message ||
          'Retention settings were modified by another user. Please reload and try again.'
      );
    }
    throw error;
  }
}

// ── Retry Settings ──────────────────────────────────────────────────

/**
 * Get current retry settings.
 */
export async function getRetrySettings(): Promise<RetrySettings> {
  const response = await apiClient.get<{ retrySettings: RetrySettings }>('/system/retry-settings');
  return response.data.retrySettings ?? { retryIntervalMinutes: 15, retryMaxAttempts: 3 };
}

/**
 * Save retry settings.
 *
 * @throws ConflictError on optimistic concurrency failure (409)
 */
export async function saveRetrySettings(settings: RetrySettings): Promise<void> {
  try {
    await apiClient.put('/system/retry-settings', { retrySettings: settings });
  } catch (error: any) {
    if (error?.response?.status === 409) {
      throw new ConflictError(
        error.response.data?.message ||
          'Retry settings were modified by another user. Please reload and try again.'
      );
    }
    throw error;
  }
}

// ── Permissions ─────────────────────────────────────────────────────

/**
 * Get the current user's resolved tab permissions.
 * This endpoint requires only authentication (any authenticated user).
 */
export async function getPermissions(): Promise<{ role: string; permissions: TabPermissions }> {
  const response = await apiClient.get<{ role: string; permissions: TabPermissions }>('/system/permissions');
  return response.data;
}
